import React, { useEffect } from 'react'

import {
  Button,
  Checkbox,
  CurrencyInput,
  Fieldset,
  InfiniteLoad,
  InputUI,
  ModalBox,
  PriceString,
  RadioButton,
  Tooltip,
  useModalContext,
  useToastsContext,
} from '@fourthwall/components'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import { useMemberChargesSubscriptionsInfiniteQuery } from '../../hooks/useMemberChargesSubscriptionsInfiniteQuery'
import type { useMemberQuery } from '../../hooks/useMemberQuery'
import { formatCurrencyFromDecimal } from '../../utils'
import { RefundModalSkeleton } from './RefundModalSkeleton'
import { disabledChargeReasonsMap } from './consts'
import { useRefundModal } from './hooks/useRefundModal'
import * as S from './styled'
import { CancelRequestType, FormFields, RefundRequestType } from './types'

type Props = {
  member: NonNullable<ReturnType<typeof useMemberQuery>['memberQuery']['data']>
  onSuccess: () => void
}

export const RefundModal = ({ member, onSuccess }: Props) => {
  const modal = useModalContext()
  const toast = useToastsContext()
  const { form, refundAmount, isLoading } = useRefundModal({
    onSuccess: (cancelRequestType) => {
      let copy = 'Refund issued.'

      if (cancelRequestType === 'Now') {
        copy =
          'Refund issued and subscription cancelled. Please allow up to a minute for changes to show on this page.'
      }

      if (cancelRequestType === 'Schedule') {
        copy =
          'Refund issued and subscription scheduled to cancel. Please allow up to a minute for changes to show on this page.'
      }

      toast.notify(copy, { type: 'info' })
      modal.close()
      onSuccess()
    },
  })

  const { memberChargesSubscriptionsInfiniteQuery } = useMemberChargesSubscriptionsInfiniteQuery(
    member.id,
  )

  // Select last charge if it is allowed
  useEffect(() => {
    if (memberChargesSubscriptionsInfiniteQuery.data && !form.values.transaction) {
      const charges = memberChargesSubscriptionsInfiniteQuery.data.pages.flatMap(
        (page) => page.charges,
      )

      const lastCharge = charges[0]

      if (lastCharge && lastCharge.refundRequest.type === 'Allowed') {
        form.setFieldValue(FormFields.transaction, lastCharge)
      }
    }
  }, [memberChargesSubscriptionsInfiniteQuery.data])

  if (!memberChargesSubscriptionsInfiniteQuery.data) return <RefundModalSkeleton member={member} />

  return (
    <ModalBox
      title={<>Refund {member.nickname}</>}
      size="medium"
      buttons={[
        <Button label="Close" appearance="secondary" onClick={modal.close} />,
        <Button
          label={
            refundAmount && form.values.transaction ? (
              <>
                Refund{' '}
                <PriceString
                  value={refundAmount}
                  currency={form.values.transaction?.currency.code}
                />
              </>
            ) : (
              'Refund'
            )
          }
          appearance="destructive"
          loading={isLoading}
          onClick={form.submitForm}
        />,
      ]}
      onClose={modal.close}
    >
      <S.Container>
        <Fieldset>
          <Fieldset.Row>
            <S.Bold>Select a transaction to refund</S.Bold>
          </Fieldset.Row>
          <Fieldset.Row>
            <S.Transactions>
              {memberChargesSubscriptionsInfiniteQuery.data.pages
                .flatMap((page) => page.charges)
                .map((charge) => (
                  <S.Transation
                    key={charge.id}
                    disabled={charge.refundRequest.type === 'Disabled'}
                    onClick={() =>
                      charge.refundRequest.type === 'Disabled'
                        ? undefined
                        : form.setFieldValue(FormFields.transaction, charge)
                    }
                  >
                    <RadioButton
                      label={
                        charge.refundRequest.type === 'Disabled' ? (
                          <Tooltip
                            reference={
                              <S.TransactionLabel>
                                <PriceString
                                  value={formatCurrencyFromDecimal(
                                    charge.amount,
                                    charge.currency.decimalPlaces,
                                  )}
                                  currency={charge.currency.code}
                                />
                                <TimezonedDateString date={charge.createdAt} format="MMM D, YYYY" />
                                Membership charge
                              </S.TransactionLabel>
                            }
                            strategy="fixed"
                            offset={[0, 0]}
                          >
                            {disabledChargeReasonsMap[charge.refundRequest.reason]}
                          </Tooltip>
                        ) : (
                          <S.TransactionLabel>
                            <PriceString
                              value={formatCurrencyFromDecimal(
                                charge.amount,
                                charge.currency.decimalPlaces,
                              )}
                              currency={charge.currency.code}
                            />
                            <TimezonedDateString date={charge.createdAt} format="MMM D, YYYY" />
                            Membership charge
                          </S.TransactionLabel>
                        )
                      }
                      checkedValue={charge.id.toString()}
                      value={form.values.transaction?.id.toString()}
                      disabled={charge.refundRequest.type === 'Disabled' || isLoading}
                      onChange={() => form.setFieldValue(FormFields.transaction, charge)}
                    />
                    {charge.refunds.length > 0 &&
                      charge.refunds.map((refund, index) => {
                        return (
                          <S.Refund key={index}>
                            <PriceString
                              value={formatCurrencyFromDecimal(
                                refund.amount,
                                charge.currency.decimalPlaces,
                              )}
                              currency={charge.currency.code}
                            />{' '}
                            refunded
                            {refund.createdAt && (
                              <>
                                {' '}
                                on{' '}
                                <TimezonedDateString
                                  date={refund.createdAt!}
                                  format="MMM D, YYYY"
                                />
                              </>
                            )}
                          </S.Refund>
                        )
                      })}
                  </S.Transation>
                ))}
              {memberChargesSubscriptionsInfiniteQuery.hasNextPage && (
                <S.LoaderContainer>
                  <InfiniteLoad next={memberChargesSubscriptionsInfiniteQuery.fetchNextPage} />
                </S.LoaderContainer>
              )}
            </S.Transactions>
          </Fieldset.Row>
          {form.errors.transaction && (
            <Fieldset.Row>
              <InputUI.Error>{form.errors.transaction}</InputUI.Error>
            </Fieldset.Row>
          )}
        </Fieldset>
        {form.values.transaction?.refundRequest.type === 'Allowed' && (
          <>
            <Fieldset>
              <Fieldset.Row>
                <S.Bold>Full or partial refund?</S.Bold>
              </Fieldset.Row>
              {form.values.transaction.refundRequest.options.full && (
                <Fieldset.Row>
                  <RadioButton
                    label={
                      <>
                        Full refund (
                        <PriceString
                          value={formatCurrencyFromDecimal(
                            form.values.transaction.refundRequest.options.full.amount,
                            form.values.transaction.currency.decimalPlaces,
                          )}
                          currency={form.values.transaction.currency.code}
                        />
                        )
                      </>
                    }
                    checkedValue={RefundRequestType.full}
                    disabled={isLoading}
                    {...form.getFieldProps(FormFields.refundRequestType)}
                  />
                </Fieldset.Row>
              )}
              {form.values.transaction.refundRequest.options.prorated.type !== 'NotAllowed' && (
                <Fieldset.Row>
                  <RadioButton
                    label={
                      <>
                        Pro-rated refund (
                        <PriceString
                          value={formatCurrencyFromDecimal(
                            form.values.transaction.refundRequest.options.prorated.amount,
                            form.values.transaction.currency.decimalPlaces,
                          )}
                          currency={form.values.transaction.currency.code}
                        />
                        , based on {form.values.transaction.refundRequest.options.prorated.daysLeft}{' '}
                        out of {form.values.transaction.refundRequest.options.prorated.daysTotal}{' '}
                        days unused)
                      </>
                    }
                    checkedValue={RefundRequestType.prorated}
                    disabled={
                      form.values.transaction.refundRequest.options.prorated.type === 'Disabled' ||
                      isLoading
                    }
                    {...form.getFieldProps(FormFields.refundRequestType)}
                  />
                </Fieldset.Row>
              )}
              {form.values.transaction.refundRequest.options.custom && (
                <>
                  <Fieldset.Row>
                    <RadioButton
                      label="Custom amount"
                      checkedValue={RefundRequestType.custom}
                      disabled={isLoading}
                      {...form.getFieldProps(FormFields.refundRequestType)}
                    />
                  </Fieldset.Row>
                  {form.values.refundRequestType === RefundRequestType.custom && (
                    <Fieldset.Row inner>
                      <CurrencyInput
                        label="Custom amount"
                        disabled={isLoading}
                        prepend={form.values.transaction.currency.code}
                        {...form.getFieldProps(FormFields.customAmount)}
                      />
                    </Fieldset.Row>
                  )}
                </>
              )}
            </Fieldset>
            {member.cancelRequest.type === 'Allowed' && (
              <Fieldset>
                <Fieldset.Row>
                  <S.Bold>Do you want to cancel this member’s subscription?</S.Bold>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Checkbox
                    label={
                      <>
                        Cancel {member.nickname}'s {member.tier?.name} subscription
                      </>
                    }
                    disabled={isLoading}
                    {...form.getFieldProps(FormFields.cancelSubscription)}
                  />
                </Fieldset.Row>
                {form.values.cancelSubscription && (
                  <>
                    <Fieldset.Row inner>
                      <RadioButton
                        label={
                          <>
                            Remove access at end of cycle
                            <>
                              &nbsp;
                              <TimezonedDateString
                                date={member.cancelRequest.subscriptionCurrentPeriodEnd}
                                format="(MMM D, YYYY)"
                              />
                            </>
                          </>
                        }
                        checkedValue={CancelRequestType.Schedule}
                        disabled={isLoading}
                        {...form.getFieldProps(FormFields.cancelRequestType)}
                      />
                    </Fieldset.Row>
                    <Fieldset.Row inner>
                      <RadioButton
                        label="Remove access immediately"
                        checkedValue={CancelRequestType.Now}
                        disabled={isLoading}
                        {...form.getFieldProps(FormFields.cancelRequestType)}
                      />
                    </Fieldset.Row>
                  </>
                )}
              </Fieldset>
            )}
            <p>
              Refunds cannot be undone. The refund amount{' '}
              {refundAmount ? (
                <>
                  of{' '}
                  <PriceString
                    value={refundAmount}
                    currency={form.values.transaction.currency.code}
                  />{' '}
                </>
              ) : undefined}
              will be deducted from your account balance. Refunds will be returned within 48 hours
              to the original payment method used.
            </p>
          </>
        )}
      </S.Container>
    </ModalBox>
  )
}
